import React from 'react';
import { Box } from '@mui/material';
import { SlDialog, SlDialogRoot } from 'src/components/BeaconV2Components/SlDialog';
import { useStacklineTheme } from '@stackline/ui';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import color from 'src/utils/colors';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
}

/**
 * Reusable confirmation dialog component
 * Used for delete, archive, and other confirmation actions
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  isLoading = false
}) => {
  const theme = useStacklineTheme();

  return (
    <SlDialogRoot>
      <SlDialog onClose={onClose} open={open} width="500px" height="auto">
        <Flex height="100%" flexDirection="column" position="relative" padding="24px">
          {/* Header Section */}
          <Flex flexDirection="column" position="relative" paddingBottom="12px">
            <Text sx={{ fontWeight: 700, fontSize: '20px', lineHeight: '28px', color: color.darkBlue }}>{title}</Text>
            <Box position="absolute" right="0" top="0">
              <button
                onClick={onClose}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CloseIcon style={{ width: '16px', height: '16px', display: 'block' }} />
              </button>
            </Box>
          </Flex>

          {/* Content Section */}
          <Flex flexDirection="column" paddingBottom="20px">
            <Text variant="body2" sx={{ lineHeight: '20px', color: color.darkBlue }}>
              {message}
            </Text>
          </Flex>

          {/* Bottom Section with Buttons */}
          <Flex flexDirection="column">
            <Flex justifyContent="flex-end" gap={16}>
              <SlButton
                onClick={onClose}
                variant="normal"
                disabled={isLoading}
                buttonSx={{
                  height: '32px',
                  padding: '0 20px',
                  fontWeight: 500,
                  fontSize: '14px',
                  border: `1px solid ${color.graysSecondary}`,
                  backgroundColor: 'transparent',
                  color: color.darkBlue
                }}
              >
                {cancelButtonText}
              </SlButton>
              <SlButton
                onClick={onConfirm}
                variant="contained"
                disabled={isLoading}
                buttonSx={{
                  height: '32px',
                  padding: '0 20px',
                  fontWeight: 500,
                  fontSize: '14px',
                  backgroundColor: color.darkBlue,
                  color: color.white,
                  border: `1px solid ${color.darkBlue}`
                }}
              >
                {confirmButtonText}
              </SlButton>
            </Flex>
          </Flex>
        </Flex>
      </SlDialog>
    </SlDialogRoot>
  );
};
